import React, { useState, useEffect, useMemo } from 'react'

import {
  Alert,
  Info,
  Popover,
  DSInputType,
  Input,
  Checkbox,
  SlimButton,
  Typography,
  Icon
} from 'components'
import * as S from './styles'
import { useSelector } from 'store/store'

import { Color } from 'store/ProductContext/productContextTypes'
import { useDispatch } from 'react-redux'
import { setMaterialFilter } from 'store/ProductContext'
import { ProductContextState } from 'container/ProductPage'
import { useFormContext, useWatch } from 'react-hook-form'
import { useMediaQuery } from '@mui/material'
import { theme } from 'styles/theme/hav-theme'
import { TypesValidations, ValidationOptions } from 'constants/enums'
import { formatDate } from 'commons/utils/date'

export const BOX_TYPE = {
  CLOSED: 'FECHADA',
  GRID: 'GRADE'
}

interface ProductDetailProps {
  convertAmount: number
}

function ProductDetail({ convertAmount }: ProductDetailProps) {
  const { materialSelected, filter: materialFilter } = useSelector(
    ({ products }) => products
  )

  const dispatch = useDispatch()
  const { control, reset } = useFormContext()
  const state = useWatch({
    control: control
  })

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  function resetedSizes(key: string) {
    const size = {} as { [y: string]: number }
    for (const color in state[key]) {
      if (
        ![
          'cd_cent_matr',
          'percentualDesconto',
          'fl_caix_grad',
          'qt_caix_grad',
          'disabledColorsRow',
          'dirtyFieldsByColor',
          'disabled_fl_caix_grad'
        ].includes(key)
      ) {
        size[color] = 0
      }
    }
    return size
  }

  const resetObject = {} as ProductContextState

  for (const key in state) {
    if (
      ![
        'cd_cent_matr',
        'percentualDesconto',
        'fl_caix_grad',
        'qt_caix_grad',
        'disabledColorsRow',
        'dirtyFieldsByColor',
        'disabled_fl_caix_grad'
      ].includes(key)
    ) {
      resetObject[key] = resetedSizes(key)
    } else {
      resetObject[key] = state[key]
    }
  }

  const [filter, setFilter] = React.useState('')
  const [records, setRecords] = useState<Color[]>(materialSelected?.cores)

  const open = Boolean(anchorEl)
  const id = open ? 'simple-popover' : undefined
  const matchLG = useMediaQuery(`(max-width: ${theme.breakpoints.lg}px)`)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleCheckMultiple = (
    event: React.ChangeEvent<HTMLInputElement>,
    cd_cor: string
  ) => {
    const newCores = records?.map((cor) => {
      if (cor.cd_cor === cd_cor) {
        return { ...cor, checked: event.target.checked }
      }
      return cor
    })
    setRecords(newCores)
  }

  const handleFilter = () => {
    const arrayRecordDsCor: string[] = []
    records &&
      records.filter((record: Color) => {
        if (record.checked) {
          // TODO: Refactor to use cd_cor instead of ds_cor
          arrayRecordDsCor.push(record.ds_cor.toLowerCase())
        }
        return arrayRecordDsCor
      })
    dispatch(setMaterialFilter(arrayRecordDsCor))
    handleClose()
    setFilter('')
  }

  const clearFilter = () => {
    setFilter('')
    const newCores = records?.map((cor) => {
      return { ...cor, checked: false }
    })
    setRecords(newCores)
    dispatch(setMaterialFilter([]))
    handleClose()
  }

  const getCheckStatus = (cd_cor: string) => {
    let checkStatus = false
    records?.map((cor) => {
      if (cor.cd_cor === cd_cor) {
        checkStatus = cor.checked || false
      }
    })
    return checkStatus
  }

  const updateDate = useMemo(() => {
    const date = new Date()
    const day = formatDate(date, 'DD/MM')
    const time = formatDate(date, 'HH[h]mm')
    return `Dados atualizados em ${day} às ${time}`
  }, [materialSelected])

  useEffect(() => {
    if (materialFilter.length > 0) {
      const newCores = records?.map((cor) => ({ ...cor, checked: true }))
      setRecords(newCores)
    }
  }, [])

  const canShowAlert =
    shouldShowMultiplicityError(
      materialSelected.ds_tipo_caix,
      materialSelected.fl_verf_sku,
      state,
      convertAmount
    ) && materialSelected.ds_tipo_caix === BOX_TYPE.CLOSED

  const filtererdData = materialSelected?.cores?.filter(
    (cor) =>
      cor.cd_cor?.includes(filter) ||
      cor.ds_cor?.toLowerCase().includes(filter.toLowerCase())
  )
  const noContent = filtererdData.length <= 0
  const hasCheck = records.some((rec) => rec.checked)
  const mixedBoxView = materialSelected.fl_digi_caix_vari === TypesValidations.YES
  return (
    <>
      <S.ProductDetailContainer
        mixedBoxView={state.fl_caix_grad}
        showAlert={canShowAlert}
      >
        <S.LastUpdateWrapper>
          <span>Digitação de material</span>
          <S.LastUpdateContainer>
            <Typography design="hav-h8-text">{updateDate}</Typography>
          </S.LastUpdateContainer>
        </S.LastUpdateWrapper>
        {canShowAlert && (
          <S.AlertContainer>
            <Alert
              title={
                state.fl_caix_grad
                  ? `Quantidade de produtos inválida. O número de produtos deve sempre ser exato: ${convertAmount} pares de uma só cor, por caixa.`
                  : `Quantidade inválida. O número digitado deve ser múltiplo de ${convertAmount}.`
              }
              kind="error"
              fullWidth={state.fl_caix_grad ? true : matchLG}
            />
          </S.AlertContainer>
        )}
        {state.fl_caix_grad && (
          <S.MixedBoxHelper>
            <strong>Especificações da Caixa:</strong>
            <p>
              A caixa grade variável deve conter exatos {convertAmount} pares. O valor
              descrito entre parênteses “()” exibe o total de pares por tamanho,
              distribuídos entre a quantidade de caixas.
            </p>
          </S.MixedBoxHelper>
        )}

        <S.DetailsContainer>
          <S.TypingBy>
            {!mixedBoxView && (
              <Info placement="top-end" iconSize={15}>
                {`Fator de conversão da caixa múltiplo de
                ${materialSelected?.qt_item_conv || '12 ou 24'}.`}
              </Info>
            )}
            <span id="SelectTitle">
              Digitar por{' '}
              {materialSelected.ds_tipo_caix === BOX_TYPE.CLOSED ? 'pares' : 'caixas'}
            </span>
          </S.TypingBy>

          <SlimButton
            type="button"
            variant="white"
            content="Limpar campos"
            icon={<Icon icon="clear-all" size="15" />}
            onClick={() => {
              reset({ ...resetObject })
            }}
          />

          <S.WrapBoxFilter>
            <SlimButton
              type="button"
              variant="white"
              id="btn-filter-colors"
              content="Filtrar cores"
              icon={<Icon icon="filter" size="15" />}
              onClick={handleClick}
            />
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl as null | undefined}
              onClose={handleClose}
            >
              <S.ContentPopoverHeader>
                <Input
                  id="input-filter-colors"
                  design={DSInputType.SEARCH_INPUT}
                  value={filter}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setFilter(event.target.value)
                  }
                  addorment
                  InputProps={{
                    placeholder: 'Buscar por cor',
                    fullWidth: true
                  }}
                />
              </S.ContentPopoverHeader>
              <S.ContentWrapperPopover>
                <S.ContentPopover>
                  {!noContent ? (
                    filtererdData.map((item) => {
                      return (
                        <div key={item.cd_cor}>
                          <Checkbox
                            size="small"
                            id={`checkbox-filter-colors-${item.cd_cor}`}
                            checked={getCheckStatus(item.cd_cor)}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                              handleCheckMultiple(event, item.cd_cor)
                            }
                          />
                          <span id="CheckboxItem">
                            {item.cd_cor} | {item.ds_cor}
                          </span>
                        </div>
                      )
                    })
                  ) : (
                    <S.NoResultFindBox>
                      <Icon icon="search-off" size="42" />
                      <span id="NoItemFoundTitle">Nenhum resultado encontrado</span>
                      <span id="NoItemFoundDescription">
                        Confira o que digitou e tente novamente.
                      </span>
                    </S.NoResultFindBox>
                  )}
                </S.ContentPopover>
              </S.ContentWrapperPopover>
              <S.DividerStyled />
              <S.ContentPopoverButton>
                <SlimButton
                  id="btn-clear-filter-colors"
                  content="Limpar filtro"
                  variant="white"
                  onClick={clearFilter}
                  disabled={noContent || !hasCheck}
                />
                <SlimButton
                  id="btn-apply-filter-colors"
                  content="Aplicar filtro"
                  variant="primary"
                  disabled={noContent || !hasCheck}
                  onClick={handleFilter}
                />
              </S.ContentPopoverButton>
            </Popover>
          </S.WrapBoxFilter>
        </S.DetailsContainer>
      </S.ProductDetailContainer>
    </>
  )
}

export default ProductDetail

export function shouldShowMultiplicityError(
  boxType: string,
  validation: ValidationOptions,
  state: ProductContextState,
  convertAmount: number
) {
  if (Array.isArray(state?.dirtyFieldsByColor) && state.fl_caix_grad) {
    if (!state?.dirtyFieldsByColor.length) return false
    const dirtyField = state.dirtyFieldsByColor[0] as unknown as string
    const dirtyColorValues = Object.entries(state).filter(
      (item) => item[0] === dirtyField
    )[0][1]
    const rowValue = Object.values(dirtyColorValues).reduce((acc, curr) => {
      return Number(curr) + acc
    }, 0)

    return rowValue !== convertAmount
  }
  if (validation === TypesValidations.YES && boxType === BOX_TYPE.CLOSED) {
    return Object.values(state)
      .filter((val) => !Array.isArray(val) && typeof val === 'object')
      .flatMap((color) => Object.values(color))
      .some((value) => Number(value) % convertAmount !== 0)
  }
  if (validation === TypesValidations.NO && boxType === BOX_TYPE.CLOSED) {
    return Object.values(state)
      .filter((val) => !Array.isArray(val) && typeof val === 'object')
      .some((value) => {
        const rowValue = Object.values(value).reduce((acc, curr) => {
          return Number(curr) + acc
        }, 0)
        return rowValue % convertAmount !== 0
      })
  }

  return false
}
